// Internal modules
import { appStore, dispatchFormAction } from '@/store';
import { CI, getContent, logger } from '@/lib';
import {
  CRS_FIRST_NAME_ID,
  CRS_LAST_NAME_ID,
  CRS_TAX_DETAILS_FIELDS,
  CRS_TAXPAYERID_OPTIONS,
  FPTI_INVALID_VALUE_ERROR,
  FPTI_VALUE_MISSING_ERROR,
  NO_TAX_ID_REASON_ADD_EXPLANATION,
  PAYS_TAXES_OUTSIDE_COUNTRY,
  SET_LEGAL_FIRST_NAME,
  SET_LEGAL_LAST_NAME,
  SET_TAX_DETAILS,
  SET_TAX_STATUS,
} from './constants';
import { updateStateIfEmpty, updateTaxPayerIdValueIfInvalid } from './utils';
import { logCrsErrors } from './fpti';
import { getCrsFields } from '../../store/models/utils';
import { getLocale } from '../../lib/globals';
import { CRS_NO_TIN_COUNTRIES } from '../../lib/constants';

/**
 * @description: Adds a new tax residency to the CRS tax details list
 * @param {Object} crsTaxDetails crs tax details
 * @returns {void}
 */
const addNewTaxResidencyChangeHandler = ({ crsTaxDetails }) => {
  /// Check for validation errors before adding new tax residency
  const isTaxFieldsValid = validateCRSFormFields();
  if (isTaxFieldsValid) {
    logger.info('crs_form_fields_validation_success');
    const updatedState = [...crsTaxDetails];
    updatedState.push(getCrsFields());
    dispatchFormAction({
      field: 'crsTaxDetails',
      payload: { value: updatedState },
    });
    logger.fpti('crs_add_country_and_tax_details', {
      state_name: 'crs_section',
      total_tax_residencies: updatedState.length,
      transition_name: 'process_add_country_click',
    });
    logger.info('added_new_tax_residency');
  }
};

/**
 * @description: Validates CRS Tax Details fields
 * @returns {Boolean} isTaxDetailsValid
 */

const validateCRSFormFields = () => {
  const { COUNTRY_CODE, TAX_PAYER_ID_VALUE, NO_TAX_PAYER_ID_REASON } =
    CRS_TAX_DETAILS_FIELDS;
  const { form: formState } = appStore.getState();
  const crsTaxDetails = formState.crsTaxDetails.value;
  let isTaxDetailsValid = true;
  const updatedState = [];

  /// Check if any existing TIN fields have backend validation error messages
  const hasExistingErrors = crsTaxDetails.some(
    taxDetail => taxDetail.taxPayerIdValue.displayError,
  );

  if (hasExistingErrors) {
    logger.info('weasley_user_attempted_submit_with_tin_server_errors');
    logger.fpti('weasley_submission_blocked_by_tin_server_errors', {
      int_error_code: '3',
      int_error_desc: 'user_attempted_submit_with_tin_server_errors',
      state_name: 'crs_section',
      transition_name: 'process_valid_tax_details',
    });
    return false;
  }

  /// Get the buyer's country
  const onboardingCountry = getLocale().country;

  /**
   * This block here validates CRS Tax Details: Country,
   * Taxpayer ID value, Reason for not having a tax id
   */
  crsTaxDetails.forEach((taxCountryDetail, index) => {
    const { taxPayerIdType } = taxCountryDetail;
    let { countryCode, taxPayerIdValue, noTaxPayerIdReason } = taxCountryDetail;

    const shouldSkipTaxIdValidation =
      CRS_NO_TIN_COUNTRIES.includes(countryCode.value) &&
      onboardingCountry === countryCode.value;

    // Check if country code was selected
    [countryCode, isTaxDetailsValid] = updateStateIfEmpty({
      content: 'crs.errors.taxResidencyCountryRequired',
      fieldNameForLogging: `${COUNTRY_CODE}_${index}`,
      fieldValues: countryCode,
      isTaxDetailsValid,
    });

    if (
      taxPayerIdType !== CRS_TAXPAYERID_OPTIONS.NO_TAXPAYER_ID &&
      !shouldSkipTaxIdValidation
    ) {
      const taxPayerIdValuelog = `${TAX_PAYER_ID_VALUE}_${index}`;

      // Check if taxpayer id value was added
      [taxPayerIdValue, isTaxDetailsValid] = updateStateIfEmpty({
        content: 'crs.errors.tinRequired',
        fieldNameForLogging: taxPayerIdValuelog,
        fieldValues: taxPayerIdValue,
        isTaxDetailsValid,
      });
      // Check if taxpayer id value is valid according to the country
      [taxPayerIdValue, isTaxDetailsValid] = updateTaxPayerIdValueIfInvalid({
        countryCode,
        fieldNameForLogging: taxPayerIdValuelog,
        isTaxDetailsValid,
        taxPayerIdValue,
      });
    }
    // In case user has no TIN, we need to check if reason was provided
    else if (!shouldSkipTaxIdValidation) {
      [noTaxPayerIdReason, isTaxDetailsValid] = updateStateIfEmpty({
        content: 'crs.errors.reason',
        fieldNameForLogging: `${NO_TAX_PAYER_ID_REASON}_${index}`,
        fieldValues: noTaxPayerIdReason,
        isTaxDetailsValid,
      });
    }

    updatedState.push({
      ...taxCountryDetail,
      countryCode,
      noTaxPayerIdReason,
      taxPayerIdType,
      taxPayerIdValue,
    });
  });

  if (!isTaxDetailsValid) {
    dispatchFormAction({
      field: 'crsTaxDetails',
      payload: { value: updatedState },
    });
    logger.info('crs_form_fields_validation_failed');
  }

  /// In case, all tax details are valid, log FPTI event for success tax details
  if (isTaxDetailsValid) {
    logger.fpti('crs_valid_tax_details', {
      state_name: 'crs_section',
      transition_name: 'process_valid_tax_details',
      valid_tax_residencies: updatedState.length,
    });
    logger.info('crs_form_fields_validation_success');
  }

  return isTaxDetailsValid;
};

/**@deprecated as part of the new inline implementation for CRS tax collection */
/// This will be removed as part of a clean up PR.
const hideInterstitialAndContinue = () => {
  const { ui: uiActions } = appStore.getActions();
  logger.fpti('crs_interstitial', {
    state_name: 'crs_interstitial',
    transition_name:
      'close_crs_interstitial_on_certify_and_create_account_click',
  });

  uiActions.toggleIsCRSInterstitialOpen(false);
  // set true so on next onboardAccount(), now done by CRS Interstitial
  // instead of Address interstitial/default submitHandler, it would pass
  const { form: formActions } = appStore.getActions();
  formActions.updateBypassCRSInterstitial(true);
  /// Commenting this to avoide circular dependency.
  /// This will be removed as part of a clean up PR.
  //onboardAccount();
};

/**
 * @param {Object} state crs redux state
 * @param {function} dispatch  dispatch
 * @returns {Boolean} isValid
 * @description: Validate CRS First and Last Name with
 * Billing First and Last name. Both of them need to match.
 */
/**@deprecated as part of the new inline implementation for CRS tax collection */
const validateCRSNames = (state, dispatch) => {
  const { form: formState } = appStore.getState();
  const { firstName, lastName } = formState;
  const { crsLegalFirstName, crsLegalLastName } = state;

  const firstNameMatch =
    firstName.value?.trim().toLowerCase() ===
    crsLegalFirstName.value?.trim().toLowerCase();
  const lastNameMatch =
    lastName.value?.trim().toLowerCase() ===
    crsLegalLastName.value?.trim().toLowerCase();

  if (firstNameMatch && lastNameMatch) {
    return true;
  }

  const dispatchDisplayError = (type, previusNameState) =>
    dispatch({
      type,
      value: {
        ...previusNameState,
        displayError: true,
      },
    });

  if (!firstNameMatch) {
    dispatchDisplayError(SET_LEGAL_FIRST_NAME, crsLegalFirstName);
    logCrsErrors({
      error: FPTI_INVALID_VALUE_ERROR,
      field: CRS_FIRST_NAME_ID,
    });
  }
  if (!lastNameMatch) {
    dispatchDisplayError(SET_LEGAL_LAST_NAME, crsLegalLastName);
    logCrsErrors({
      error: FPTI_INVALID_VALUE_ERROR,
      field: CRS_LAST_NAME_ID,
    });
  }
  return false;
};

/**@deprecated as part of the new inline implementation for CRS tax collection */
const validateFormFields = ({ dispatch, state }) => {
  const {
    COUNTRY_CODE,
    TAX_PAYER_ID_TYPE,
    TAX_PAYER_ID_VALUE,
    NO_TAX_PAYER_ID_REASON,
    COMMENT,
  } = CRS_TAX_DETAILS_FIELDS;
  const { crsTaxDetails, crsTaxStatus } = state;
  let isTaxStatusValud = true;
  let isTaxDetailsValid = true;
  const updatedState = [];

  // Verify if user selected tax residency status
  if (crsTaxStatus.value === null) {
    dispatch({
      type: SET_TAX_STATUS,
      value: {
        ...crsTaxStatus,
        displayError: true,
        error: getContent('crs.errors.status'),
      },
    });
    logCrsErrors({
      error: FPTI_VALUE_MISSING_ERROR,
      field: 'tax_status',
    });
    isTaxStatusValud = false;
  } else if (crsTaxStatus.value === PAYS_TAXES_OUTSIDE_COUNTRY) {
    /**
     * This block here validates CRS Tax Details: Country, Taxpayer ID Type,
     * Taxpayer ID value, Reason for not having a tax id, additional comments
     */

    crsTaxDetails.forEach((country, index) => {
      let {
        countryCode,
        taxPayerIdType,
        taxPayerIdValue,
        noTaxPayerIdReason,
        comment,
      } = country;

      // Check if country code was selected
      [countryCode, isTaxDetailsValid] = updateStateIfEmpty({
        content: 'crs.errors.required',
        fieldNameForLogging: `${COUNTRY_CODE}_${index}`,
        fieldValues: countryCode,
        isTaxDetailsValid,
      });

      // Check if taxpayer id type was selected
      [taxPayerIdType, isTaxDetailsValid] = updateStateIfEmpty({
        content: 'crs.errors.taxpayerID',
        fieldNameForLogging: `${TAX_PAYER_ID_TYPE}_${index}`,
        fieldValues: taxPayerIdType,
        isTaxDetailsValid,
      });

      if (
        taxPayerIdType.value === CRS_TAXPAYERID_OPTIONS.SSN ||
        taxPayerIdType.value === CRS_TAXPAYERID_OPTIONS.TIN
      ) {
        const taxPayerIdValuelog = `${TAX_PAYER_ID_VALUE}_${index}`;
        // Check if taxpayer id value was added
        [taxPayerIdValue, isTaxDetailsValid] = updateStateIfEmpty({
          content: 'crs.errors.invalidTaxID.default',
          fieldNameForLogging: taxPayerIdValuelog,
          fieldValues: taxPayerIdValue,
          isTaxDetailsValid,
        });
        // Check if taxpayer id value is valid according to the country
        [taxPayerIdValue, isTaxDetailsValid] = updateTaxPayerIdValueIfInvalid({
          countryCode,
          fieldNameForLogging: taxPayerIdValuelog,
          isTaxDetailsValid,
          taxPayerIdType,
          taxPayerIdValue,
        });
      } else if (
        taxPayerIdType.value === CRS_TAXPAYERID_OPTIONS.NO_TAXPAYER_ID
      ) {
        // check if reason was selected
        [noTaxPayerIdReason, isTaxDetailsValid] = updateStateIfEmpty({
          content: 'crs.errors.reason',
          fieldNameForLogging: `${NO_TAX_PAYER_ID_REASON}_${index}`,
          fieldValues: noTaxPayerIdReason,
          isTaxDetailsValid,
        });

        if (noTaxPayerIdReason.value === NO_TAX_ID_REASON_ADD_EXPLANATION) {
          /**
           * If user selected addExplanation, we need to verify
           * if he added the explanation in the comment field
           */
          [comment, isTaxDetailsValid] = updateStateIfEmpty({
            content: 'crs.errors.required',
            fieldNameForLogging: `${COMMENT}_${index}`,
            fieldValues: comment,
            isTaxDetailsValid,
          });
        }
      }

      updatedState.push({
        ...country,
        comment,
        countryCode,
        noTaxPayerIdReason,
        taxPayerIdType,
        taxPayerIdValue,
      });
    });

    if (!isTaxDetailsValid) {
      dispatch({
        type: SET_TAX_DETAILS,
        value: updatedState,
      });
    }
  }

  const isCRSNameValid = validateCRSNames(state, dispatch);

  return isTaxDetailsValid && isCRSNameValid && isTaxStatusValud;
};

/**
 * @description: Removes leading/trailing white spaces from taxpayer id values
 * @param {Object} crsTaxDetails
 * @returns crsTaxDetails
 */
const trimTaxIdValues = crsTaxDetails => {
  crsTaxDetails.forEach(taxDetail => {
    taxDetail.taxPayerIdValue.value = taxDetail.taxPayerIdValue.value.trim();
  });
  return crsTaxDetails;
};

/**@deprecated as part of the new inline implementation for CRS tax collection */
const createAccountHandler = ({ dispatch, state }) => {
  logger.fpti('crs_certify_and_create_account', {
    state_name: 'crs_interstitial',
    transition_name: 'process_certify_and_create_account_click',
  });
  const isValid = validateFormFields({ dispatch, state });

  if (isValid) {
    const { crsTaxDetails, crsTaxStatus, crsLegalFirstName, crsLegalLastName } =
      state;
    // We only update the state once everything is valid
    dispatchFormAction({
      field: 'crsTaxDetails',
      payload: { value: trimTaxIdValues(crsTaxDetails) },
    });
    dispatchFormAction({
      field: 'crsTaxStatus',
      payload: crsTaxStatus,
    });

    dispatchFormAction({
      field: 'crsLegalFirstName',
      payload: { ...crsLegalFirstName, value: crsLegalFirstName.value.trim() },
    });

    dispatchFormAction({
      field: 'crsLegalLastName',
      payload: { ...crsLegalLastName, value: crsLegalLastName.value.trim() },
    });

    CI.ok();
    hideInterstitialAndContinue();
  }
};

export {
  createAccountHandler,
  hideInterstitialAndContinue,
  trimTaxIdValues,
  validateCRSNames,
  validateFormFields,
  addNewTaxResidencyChangeHandler,
  validateCRSFormFields,
};
